import { leadTrainerNames, assistantTrainers } from './trainerUtils';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import { formatDateShort } from '../../utils/dateFormat';
// Shared with L&D Records → Sessions → View: one component,
// so the two surfaces cannot drift apart.
import SessionDetailPanel from './SessionDetailPanel';
import { API_BASE_URL } from '../../utils/api';

const TrainingSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // In-place detail: expanding a session shows who
  // attended, who was absent and why, and the feedback — no tab hopping.
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null); // { loading, error, data }
  // Opens on the expected list — who should be there is the
  // first question; who turned up is the tab next door.
  const [detailTab, setDetailTab] = useState('roster');
  // The open row, so collapsing from the bottom of a long panel can bring it
  // back into view instead of dumping the reader mid-table.
  const openRowRef = useRef(null);
  // Mandatory or not is the one cut this tab offers —
  // it is a flag of its own and the only thing the 40-hour target hangs
  // off. Filtered in the browser: the endpoint hands over the whole schedule in
  // one response, so there is nothing to refetch. Wording matches the same
  // filter on Management → Training Records.
  const [filterMandatory, setFilterMandatory] = useState('all');

  // Each row carries the lead trainers' names as one sortable string and the
  // assistant count; the detail panel lists everyone with their factor.
  const filtered = useMemo(() => {
    const rows = sessions.map((s) => ({
      ...s,
      trainerNames: leadTrainerNames(s.trainers),
      assistantCount: assistantTrainers(s.trainers).length,
    }));
    if (filterMandatory === 'all') return rows;
    const want = filterMandatory === 'mandatory';
    return rows.filter((s) => !!s.isMandatory === want);
  }, [sessions, filterMandatory]);

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ld/training-sessions`, {
        method: 'GET',
        credentials: 'include', // Include HTTPOnly cookies
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch training sessions');
      }

      const data = await response.json();
      if (data.success) {
        setSessions(data.data);
        setLastUpdated(new Date(data.timestamp));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching training sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  // Closing scrolls the session row back under the reader's eye if it has been
  // left above the fold — the panel is tall enough that its collapse control
  // usually sits a screen or two below the row itself.
  const collapse = () => {
    const row = openRowRef.current;
    const offscreen = row ? row.getBoundingClientRect().top < 0 : false;
    setExpandedId(null);
    setDetail(null);
    if (offscreen) {
      requestAnimationFrame(() => row.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const toggleExpand = async (sessionId) => {
    if (expandedId === sessionId) { collapse(); return; }
    setExpandedId(sessionId);
    setDetailTab('roster');
    setDetail({ loading: true, error: null, data: null });
    try {
      const res = await fetch(`${API_BASE_URL}/api/ld/training-sessions/${sessionId}/detail`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load session detail');
      setDetail({ loading: false, error: null, data });
    } catch (e) {
      setDetail({ loading: false, error: e.message, data: null });
    }
  };

  // Esc closes the open session, the same key that closes every modal here.
  useEffect(() => {
    if (expandedId == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') collapse(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // Filtering out the open session takes its panel off the page with it, so the
  // expanded state is dropped too — otherwise coming back to All would silently
  // re-open a row the reader closed by filtering.
  useEffect(() => {
    if (expandedId != null && !filtered.some((s) => s.id === expandedId)) {
      setExpandedId(null);
      setDetail(null);
    }
  }, [filtered, expandedId]);

  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(filtered);

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading training sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="text-red-800">
          <h3 className="font-semibold">Error loading training sessions</h3>
          <p className="text-sm mt-1">{error}</p>
        </div>
        <button
          onClick={fetchSessions}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Internal training sessions schedule"
        lastUpdated={lastUpdated}
        actions={
          <button
            onClick={fetchSessions}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Filter — its own white card (docs/dashboard-ui.md §4), sitting above
          everything it governs: the cards and the table both read the filtered
          set, so a filtered page never shows a total the list cannot account for. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="ts-mandatory" className="text-sm font-medium text-gray-700">
            Mandatory
          </label>
          <select
            id="ts-mandatory"
            value={filterMandatory}
            onChange={(e) => setFilterMandatory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All</option>
            <option value="mandatory">Mandatory</option>
            <option value="non">Non-mandatory</option>
          </select>
          {filterMandatory !== 'all' && (
            <>
              <span className="text-sm text-gray-500">
                Showing {filtered.length} of {sessions.length} sessions
              </span>
              <button
                type="button"
                onClick={() => setFilterMandatory('all')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards — of whatever the filter left standing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Sessions</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{filtered.length}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Hours</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {filtered.reduce((sum, s) => sum + (s.hours || 0), 0)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Mandatory Sessions</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {filtered.filter(s => s.isMandatory).length}
          </p>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Session" sortKey="courseTitle" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Date & Time" sortKey="date" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Duration" sortKey="hours" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Category" sortKey="categoryTitle" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Trainer" sortKey="trainerNames" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="" sortKey="" sortable={false} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    {sessions.length === 0
                      ? 'No training sessions found'
                      : `No ${filterMandatory === 'mandatory' ? 'mandatory' : 'non-mandatory'} sessions`}
                  </td>
                </tr>
              ) : (
                rows.map((session, index) => {
                  const isOpen = expandedId === session.id;
                  return (
                  <React.Fragment key={session.id || session.sessionCode || index}>
                    <tr
                      ref={isOpen ? openRowRef : null}
                      className={`cursor-pointer transition-colors ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => toggleExpand(session.id)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(session.id); }
                      }}
                    >
                      <td className={`px-6 py-4 border-l-4 ${isOpen ? 'border-blue-600' : 'border-transparent'}`}>
                        <div className="text-sm font-medium text-gray-900">
                          {session.courseTitle || session.sessionCode}
                        </div>
                        <div className="text-xs text-gray-500">ID: {session.sessionCode}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDateShort(session.date)}</div>
                        <div className="text-xs text-gray-500">{session.startEndTime || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {session.hours != null ? Number(session.hours).toFixed(2) : ''} hours
                        </span>
                      </td>
                      {/* Category + the session's own Mandatory flag —
                          every session shows a category; only mandatory ones
                          get the extra pill. '—' = a row not yet
                          classified. */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          {session.categoryTitle ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {session.categoryTitle}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                          {session.isMandatory && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              Mandatory
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {session.trainerNames || 'N/A'}
                        </div>
                        {session.assistantCount > 0 && (
                          <div className="text-xs text-gray-500">
                            +{session.assistantCount} assistant{session.assistantCount === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                      {/* A worded control, not a bare chevron — a bare chevron
                          does not read as "this row is closable". */}
                      <td className="px-6 py-4 text-right">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
                            isOpen
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-white border-gray-300 text-gray-600'
                          }`}
                        >
                          {isOpen ? (
                            <>Close <ChevronUp className="w-4 h-4" /></>
                          ) : (
                            <>Details <ChevronDown className="w-4 h-4" /></>
                          )}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan="6" className="p-0 bg-blue-50/40 border-l-4 border-blue-600">
                          <div className="px-6 py-4 space-y-4">
                            {/* Header of the opened panel. It does not repeat the
                                session's name and date — the highlighted row says
                                both, one line up — nor add a second Close button
                                within a thumb's width of the row's own. It
                                only names the panel and points at Esc. */}
                            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">
                              Session Detail
                              <span className="ml-2 font-normal normal-case tracking-normal text-gray-500">
                                Esc to close
                              </span>
                            </p>

                            {detail?.loading && (
                              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                                <RefreshCw className="w-4 h-4 animate-spin" /> Loading session detail…
                              </div>
                            )}
                            {detail?.error && (
                              <div className="text-sm text-red-600 py-2">{detail.error}</div>
                            )}
                            {detail?.data && (
                              <SessionDetailPanel
                                detail={detail.data}
                                tab={detailTab}
                                onTab={setDetailTab}
                              />
                            )}

                            {/* Second way out, at the far end of a panel that can
                                run several screens long. */}
                            <button
                              type="button"
                              onClick={collapse}
                              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-blue-300 bg-white/60 text-sm font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                            >
                              <ChevronUp className="w-4 h-4" /> Collapse This Session
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default TrainingSessions;
